import { useState } from "react";
import { Link } from "react-router-dom";
import { useFetch, ago } from "@/lib/api";
import { PageHeader, ConnBadge, Loading, ErrorBox, Empty, Pill } from "@/components/common";
import { useAuth, can } from "@/context/AuthContext";

export default function Devices() {
  const { orgId, role } = useAuth();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [group, setGroup] = useState("");
  const [page, setPage] = useState(1);
  const { data, loading, error } = useFetch(`/devices?q=${encodeURIComponent(q)}&status=${status}&group_id=${group}&page=${page}&page_size=25`, [orgId]);
  const { data: groups } = useFetch("/org/groups", [orgId]);
  const gname = (id) => (groups || []).find((g) => g.id === id)?.name || "—";
  const pages = data ? Math.max(1, Math.ceil(data.total / 25)) : 1;
  return (
    <div className="fade-up">
      <PageHeader title="Devices" crumbs={["Portal", "Devices"]}>
        {can.manage(role) && <Link to="/portal/enroll" className="px-4 py-2 bg-red-600 text-white text-sm font-semibold hover:bg-red-700" data-testid="devices-enroll-btn">Enroll device</Link>}
      </PageHeader>
      <div className="flex flex-wrap gap-3 mb-4">
        <input placeholder="Search hostname…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="border border-gray-300 px-3 py-2 text-sm w-64" data-testid="devices-search-input" />
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-gray-300 px-3 py-2 text-sm" data-testid="devices-status-filter">
          <option value="">All statuses</option><option value="online">Online</option><option value="offline">Offline</option><option value="pending">Awaiting check-in</option><option value="pending_approval">Pending approval</option><option value="revoked">Revoked</option>
        </select>
        <select value={group} onChange={(e) => { setGroup(e.target.value); setPage(1); }} className="border border-gray-300 px-3 py-2 text-sm" data-testid="devices-group-filter">
          <option value="">All groups</option>{(groups || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      </div>
      <ErrorBox error={error} />
      {loading ? <Loading /> : data.items.length === 0 ? <Empty title="No devices match" text="Enrolled devices appear here after their first check-in." /> : (
        <div className="card-flat overflow-x-auto">
          <table className="data-table" data-testid="devices-table">
            <thead><tr><th>Hostname</th><th>Status</th><th>Type</th><th>OS</th><th>CPU</th><th>Memory</th><th>Min disk free</th><th>Agent</th><th>Group</th><th>Last check-in</th></tr></thead>
            <tbody>
              {data.items.map((d) => (
                <tr key={d.id} data-testid={`device-row-${d.id}`}>
                  <td><Link to={`/portal/devices/${d.id}`} className="font-semibold hover:text-red-600" data-testid={`device-link-${d.id}`}>{d.hostname}</Link>
                    {d.agent_type === "test-harness" && <div><Pill tone="waiting">synthetic test data</Pill></div>}</td>
                  <td><ConnBadge status={d.status === "revoked" ? "revoked" : d.connectivity} /></td>
                  <td className="capitalize">{d.device_type}</td>
                  <td className="text-xs">{d.os_name}<div className="text-gray-400">{d.os_build}</div></td>
                  <td className="font-mono">{d.cpu != null ? `${d.cpu}%` : "—"}</td>
                  <td className="font-mono">{d.mem != null ? `${d.mem}%` : "—"}</td>
                  <td className="font-mono">{d.min_disk_free_pct != null ? `${d.min_disk_free_pct}%` : "—"}</td>
                  <td className="font-mono text-xs">{d.agent_version}<div className="text-gray-400">{d.agent_type}</div></td>
                  <td>{gname(d.group_id)}</td>
                  <td className="text-xs whitespace-nowrap">{ago(d.last_seen)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between items-center p-3 text-sm text-gray-500">
            <span data-testid="devices-total">{data.total} devices</span>
            <div className="flex gap-2"><button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1 border disabled:opacity-40" data-testid="devices-prev-page">Prev</button><span>{page}/{pages}</span><button disabled={page >= pages} onClick={() => setPage(page + 1)} className="px-3 py-1 border disabled:opacity-40" data-testid="devices-next-page">Next</button></div>
          </div>
        </div>
      )}
    </div>
  );
}
